import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const NO_STROKES = []
const COLORS = ['#1c1c1c', '#2f6fed', '#d92d20', '#17803d']
const PEN_SIZE = 2.4
const ERASER_SIZE = 18
const MIN_HEIGHT = 160
const MAX_HEIGHT = 1600

// Ignore finger and palm input while a stylus is down, and briefly after it
// lifts, so a resting hand does not draw. A mouse or a finger alone still draws.
const PALM_MS = 700

function drawStroke(ctx, stroke, width) {
  const points = stroke.points
  if (!points.length) return
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  if (stroke.tool === 'eraser') {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.strokeStyle = 'rgba(0,0,0,1)'
    ctx.fillStyle = 'rgba(0,0,0,1)'
  } else {
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = stroke.color
    ctx.fillStyle = stroke.color
  }
  ctx.lineWidth = stroke.size
  if (points.length === 1) {
    const point = points[0]
    ctx.beginPath()
    ctx.arc(point.x * width, point.y, stroke.size / 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
    return
  }
  ctx.beginPath()
  points.forEach((point, index) => {
    const x = point.x * width
    if (index === 0) ctx.moveTo(x, point.y)
    else ctx.lineTo(x, point.y)
  })
  ctx.stroke()
  ctx.restore()
}

function paint(canvas, strokes, draft) {
  const width = canvas.clientWidth
  const height = canvas.clientHeight
  if (!width || !height) return
  const ratio = window.devicePixelRatio || 1
  const bitmapWidth = Math.round(width * ratio)
  const bitmapHeight = Math.round(height * ratio)
  if (canvas.width !== bitmapWidth || canvas.height !== bitmapHeight) {
    canvas.width = bitmapWidth
    canvas.height = bitmapHeight
  }
  const ctx = canvas.getContext('2d')
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  ctx.clearRect(0, 0, width, height)
  strokes.forEach((stroke) => drawStroke(ctx, stroke, width))
  if (draft) drawStroke(ctx, draft, width)
}

function pointOf(canvas, event) {
  const rect = canvas.getBoundingClientRect()
  return {
    x: rect.width ? (event.clientX - rect.left) / rect.width : 0,
    y: event.clientY - rect.top,
  }
}

function SketchBlock({ block, onChange }) {
  const canvasRef = useRef(null)
  const strokeRef = useRef(null)
  const activePointer = useRef(null)
  const pensDown = useRef(new Set())
  const lastPen = useRef(0)
  const resizeRef = useRef(null)
  const [tool, setTool] = useState('pen')
  const [color, setColor] = useState(COLORS[0])
  const [draft, setDraft] = useState(null)
  const [undone, setUndone] = useState([])
  const [liveHeight, setLiveHeight] = useState(null)
  const [padWidth, setPadWidth] = useState(0)

  const strokes = Array.isArray(block.strokes) ? block.strokes : NO_STROKES
  const height = liveHeight ?? block.height ?? 280

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const observer = new ResizeObserver(() => setPadWidth(canvas.clientWidth))
    observer.observe(canvas)
    setPadWidth(canvas.clientWidth)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (canvas) paint(canvas, strokes, draft)
  }, [strokes, draft, height, padWidth])

  const accept = (event) => {
    if (event.pointerType === 'pen') {
      lastPen.current = Date.now()
      return true
    }
    if (event.pointerType === 'touch' && (pensDown.current.size || Date.now() - lastPen.current < PALM_MS)) {
      return false
    }
    return true
  }

  const onPointerDown = (event) => {
    if (!accept(event) || strokeRef.current) return
    if (event.pointerType === 'pen') pensDown.current.add(event.pointerId)
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    activePointer.current = event.pointerId
    const stroke = {
      tool,
      color,
      size: tool === 'eraser' ? ERASER_SIZE : PEN_SIZE,
      points: [pointOf(event.currentTarget, event)],
    }
    strokeRef.current = stroke
    setDraft(stroke)
  }

  const onPointerMove = (event) => {
    if (event.pointerType === 'pen') lastPen.current = Date.now()
    const stroke = strokeRef.current
    if (!stroke || event.pointerId !== activePointer.current) return
    const canvas = event.currentTarget
    const width = canvas.clientWidth || 1
    const incoming = event.getCoalescedEvents?.() || [event]
    incoming.forEach((sample) => {
      const point = pointOf(canvas, sample)
      const last = stroke.points[stroke.points.length - 1]
      const dx = (point.x - last.x) * width
      const dy = point.y - last.y
      if (dx * dx + dy * dy < 0.36) return
      stroke.points.push(point)
    })
    setDraft({ ...stroke, points: stroke.points.slice() })
  }

  const finishStroke = (event) => {
    if (event.pointerType === 'pen') {
      pensDown.current.delete(event.pointerId)
      lastPen.current = Date.now()
    }
    if (event.pointerId !== activePointer.current) return
    const stroke = strokeRef.current
    strokeRef.current = null
    activePointer.current = null
    setDraft(null)
    if (!stroke || !stroke.points.length) return
    setUndone([])
    onChange({ strokes: [...strokes, stroke] })
  }

  const undo = () => {
    if (!strokes.length) return
    setUndone((prev) => [...prev, strokes[strokes.length - 1]])
    onChange({ strokes: strokes.slice(0, -1) })
  }

  const redo = () => {
    if (!undone.length) return
    const stroke = undone[undone.length - 1]
    setUndone((prev) => prev.slice(0, -1))
    onChange({ strokes: [...strokes, stroke] })
  }

  const onResizeDown = (event) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    resizeRef.current = { id: event.pointerId, y: event.clientY, height, current: height }
  }

  const onResizeMove = (event) => {
    const drag = resizeRef.current
    if (!drag || event.pointerId !== drag.id) return
    const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(drag.height + event.clientY - drag.y)))
    drag.current = next
    setLiveHeight(next)
  }

  const onResizeUp = (event) => {
    const drag = resizeRef.current
    if (!drag || event.pointerId !== drag.id) return
    resizeRef.current = null
    const next = drag.current ?? drag.height
    setLiveHeight(null)
    if (next !== block.height) onChange({ height: next })
  }

  return (
    <div className="nb-sketch">
      <div className="nb-sketch-bar" role="toolbar" aria-label="Sketch tools">
        {COLORS.map((ink) => (
          <button
            key={ink}
            type="button"
            className={'nb-sketch-swatch' + (tool === 'pen' && color === ink ? ' is-on' : '')}
            style={{ background: ink }}
            aria-label={`Pen ${ink}`}
            aria-pressed={tool === 'pen' && color === ink}
            onClick={() => {
              setTool('pen')
              setColor(ink)
            }}
          />
        ))}
        <button
          type="button"
          className={'nb-sketch-tool' + (tool === 'eraser' ? ' is-on' : '')}
          aria-pressed={tool === 'eraser'}
          onClick={() => setTool('eraser')}
        >
          Eraser
        </button>
        <span className="nb-sketch-spacer" />
        <button type="button" className="nb-sketch-tool" onClick={undo} disabled={!strokes.length}>
          Undo
        </button>
        <button type="button" className="nb-sketch-tool" onClick={redo} disabled={!undone.length}>
          Redo
        </button>
      </div>
      <div className="nb-sketch-pad" style={{ height }}>
        {!strokes.length && !draft ? <div className="nb-sketch-empty">Draw here</div> : null}
        <canvas
          ref={canvasRef}
          className="nb-sketch-canvas"
          aria-label="Sketch pad"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
          onContextMenu={(event) => event.preventDefault()}
        />
      </div>
      <button
        type="button"
        className="nb-sketch-resize"
        aria-label="Drag to make the sketch taller or shorter"
        title="Drag to resize"
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
      />
    </div>
  )
}

export default SketchBlock
